import { reactive, ref, computed, watch, onMounted } from 'vue';
import { extend } from 'vee-validate';
import { validateEvoRequirement } from '../events/evoRules';
import store from '@/store';
import { useEventFormLookups } from './useEventFormLookups';
import { useEventFormWatchers } from './useEventFormWatchers';
import { useEventFormSubmit } from './useEventFormSubmit';

/**
 * Public composable entrypoint. Composes:
 *   - useEventFormLookups  → lookup data + lookup fetching
 *   - useEventFormWatchers → 9 reactive watchers + fecha-error flags
 *   - useEventFormSubmit   → normalize/sync cesión, procesar editores,
 *                             crearEvento, submit orchestration
 *
 * Keeps a stable public surface (everything FormEvento.vue + the existing
 * 31 unit tests rely on) so callers don't break when we move concerns
 * between sub-composables.
 */
export function useEventForm(props, { emit }) {
    // === Lookup data (catalog labels kept here because they're static) ===
    const estados = ref([
        { id: 0, nombre: 'Borrador' },
        { id: 1, nombre: 'Publicado' },
        { id: 3, nombre: 'Aplazado' },
        { id: 2, nombre: 'Cancelado' }
    ]);

    const limitesTiempo = ref([
        { id: 1, nombre: '24 horas' },
        { id: 2, nombre: '48 horas' },
        { id: 3, nombre: '72 horas' }
    ]);

    // === Reactive state ===
    const textoCondicionesLegalesPreventaDefault = `
        <h3>Condiciones de Registro para la Preventa de Entradas</h3><br>
        El acceso a la preventa de entradas estará disponible únicamente para las
        personas que se hayan registrado previamente, cumplan con las
        condiciones establecidas por la organización y hayan recibido por correo
        electrónico la aceptación de su registro en la preventa.<br><br>
        El registro es personal, gratuito e intransferible. Cada usuario deberá
        completar correctamente el formulario de inscripción proporcionando
        información veraz y actualizada. La organización se reserva el derecho de
        validar los datos facilitados y de cancelar registros que presenten
        información incorrecta, incompleta o fraudulenta.<br><br>
        El registro no garantiza la obtención de entradas, ya que estas estarán
        sujetas a disponibilidad y se asignarán por orden de acceso durante el
        periodo de preventa. El acceso a la preventa se habilitará exclusivamente
        en la fecha y horario indicados previamente por la organización.<br><br>
        El enlace, código o credencial de acceso a la preventa es de uso exclusivo del
        usuario registrado y no podrá ser compartido, vendido ni cedido a terceros.
        El uso indebido podrá dar lugar a la anulación del acceso a la preventa y de
        las entradas adquiridas.<br><br>
        La organización se reserva el derecho de modificar las condiciones de la
        preventa, así como de cancelar o suspender el proceso de registro por
        causas técnicas, organizativas o de fuerza mayor, informando
        oportunamente a los usuarios registrados.<br><br>
        Al completar el registro, el usuario acepta íntegramente las presentes
        condiciones.
    `;

    const localData = reactive({
        // Defaults FIRST so that the spread below overwrites them with
        // real values when the API provides them (EDIT flow). For CREATE
        // flow props.evento only carries the 6 scalar keys, so the
        // defaults survive and the reactive Proxy has the keys declared
        // from the start. That is what makes `watch(() => localData.foo,
        // ...)` getters subscribe during setup; otherwise the getters
        // would read an undeclared key, the Proxy wouldn't track a
        // dependency, and watchers wouldn't fire when set() later
        // creates the key — exactly why "Activar entradas compartidas"
        // rendered correctly but its getEventos side-effect never ran.
        descuentos_abonados_no_cerrar_butacas_seleccionados: [],
        tipo_abono_no_cerrar_butacas: null,
        no_cerrar_butacas_combinaciones: [],
        entradas_compartidas: false,
        preventa_configurada: false,
        // Flags de error para fechas de preventa vacías (mirror de master).
        // Defaults-first para que la reactividad Vue 2.7 los rastree desde
        // el setup, evitando bugs silent-watcher.
        // 'errorFechasAccesoPreventa' / 'errorFechasVentaPreventa' (legacy
        // banner "el rango es obligatorio") ELIMINADOS: redundantes con los
        // 'rules="required"' inline de cada BaseDateTimeInput. El observer
        // de vee-validate ya bloquea submit cuando algún campo está vacío.
        permitir_cesion_temporal_asiento: false,
        cerrar_butacas_abonados: false,
        descuento_abonado: false,
        // `amigo_abonado`, `seccion` and `forzar_adyacentes` declared as
        // defaults for the same trap: these are the other three toggles in
        // EventoAbonados.vue. Their @input handlers used direct assignment
        // (val => localData.X = val) and without the defaults the key was
        // created by late assignment on the reactive Proxy, so Vue 2.7's
        // defineProperty-based reactivity didn't track them and the toggle
        // didn't update visually.
        amigo_abonado: false,
        seccion: false,
        forzar_adyacentes: false,
        // `abonado_desde`, `abonado_hasta`, `n_acompanantes_min` and
        // `n_acompanantes` declared as defaults for the same trap: these
        // are the 4 BaseInputNumber inputs inside the conditional block
        // under "descuento_abonado || amigo_abonado" in EventoAbonados.vue.
        // Their @input handlers used direct assignment and without these
        // defaults the keys were created by late assignment on the reactive
        // Proxy, so Vue 2.7's defineProperty-based reactivity didn't track
        // them and the inputs didn't update visually.
        abonado_desde: null,
        abonado_hasta: null,
        n_acompanantes_min: null,
        n_acompanantes: null,
        experiencia: false,
        // Misma trampa que arriba: 'solo_abonados' se muta vía
        // `@input="val => localData.solo_abonados = val"` en EventoSales.vue
        // y sin default Vue 2.7 no trackea la key huérfana. Bug conocido en
        // este codebase (default-first pattern).
        solo_abonados: false,
        // Misma trampa que arriba: 'compra_unica' se muta vía
        // `@input="val => localData.compra_unica = val"` en EventoSales.vue
        // y sin default Vue 2.7 no trackea la key huérfana. Bug conocido en
        // este codebase (default-first pattern).
        compra_unica: false,
        venta_online_cerrada: false,
        // Defaults declarados para `venta_online_cerrada_definitiva` y
        // `entradas_agotadas_online` por la misma trampa documentada arriba:
        // los dos ToggleSwitch en EventoSales.vue tienen `@input="val => localData.X = val"`
        // (asignación directa). Sin declarar la key en este `reactive({})`,
        // Vue 2.7 crea el key fuera del Proxy y NO lo trackea → el toggle no
        // se actualiza. Cuando el usuario hace click en otro toggle que SÍ
        // funciona y dispara re-render, la reactividad ya ve la key huérfana
        // y entonces la pinta. Sintoma clásico.
        venta_online_cerrada_definitiva: false,
        entradas_agotadas_online: false,
        // Misma trampa que arriba: 'activar_enlace_redireccion' y
        // 'enlace_redireccion' se mutan vía asignación directa en EventoConfig.vue
        // (`@input="val => localData.X = val"`). Sin default Vue 2.7 no trackea.
        activar_enlace_redireccion: false,
        enlace_redireccion: '',
        // Misma trampa que arriba: 4 ToggleSwitch en EventoConfig.vue con
        // `@input="val => localData.X = val"` y key no declarada → Vue 2.7
        // no trackea y el toggle no se actualiza al click.
        comprobacion_butacas: false,
        tiene_plazas_extra: false,
        compra_privada: false,
        calendario: false,
        // Misma trampa: ToggleSwitch 'Reserva de instalaciones' y
        // 'Enviar entradas 72h antes del evento' (cuya key se llama
        // 'no_enviar_entradas' por el default semántico invertido — el
        // master asume NO enviar salvo que se desmarque) en EventoConfig.vue.
        reserva_instalaciones: false,
        no_enviar_entradas: false,
        // Misma trampa: 'evento_barra' (sólo TEATROS) y 'texto_producto_extra'
        // (el BaseInputText que aparece cuando 'tiene_plazas_extra=true') se
        // mutan vía asignación directa en EventoConfig.vue.
        evento_barra: false,
        texto_producto_extra: null,
        // 'show_in_kiosk' (sólo renderizado si canEditKiosk) — la key es
        // exclusiva de esta rama, no existe en master. Misma trampa que
        // arriba: @input directo sin default → Vue 2.7 no trackea.
        show_in_kiosk: false,
        // Defaults declarados para que las asignaciones tard\u00edas (v\u00eda
        // direct assignment en EventoBasicInfo y el `localData.tags = ...` del
        // submit) no generen keys hu\u00e9rfanas no reactivas. Ver
        // 'show_in_kiosk' arriba para el comentario del default-first pattern.
        tags: [],
        tags_seleccionadas: [],
        permitir_liberacion_asientos: false,
        start_event_date: null,
        end_event_date: null,
        registro_preventa_desde: null,
        registro_preventa_hasta: null,
        venta_preventa_desde: null,
        venta_preventa_hasta: null,
        cuenta_atras: null,
        limite_dia_abonado: null,
        texto_condiciones_legales_preventa: null,
        provincia: null,
        location: null,
        location_id: null,
        synopsis: null,
        image: null,  // URL of the evento image (uploaded or existing).
        // Sports trio: declared as defaults so the cross-field validation
        // computed in EventoBasicInfo.vue (`sportsFieldsFilled`) subscribes
        // to these keys during setup. Without these defaults the keys don't
        // exist on the reactive Proxy at computed-creation time, so the
        // computed doesn't track a dependency and never re-evaluates when
        // the user picks a value via `@input="val => localData.foo = val"`
        // — identical gotcha to the one fixed for `entradas_compartidas`.
        equipo_local_id: null,
        equipo_visitante_id: null,
        division_id: null,
        // AvanzaEsp: declared as defaults for the same reactivity trap —
        // EventoBasicInfo's `errorEvo` computed and the picker's v-model need
        // these keys to exist on the reactive Proxy during setup, otherwise
        // the computed never subscribes and the message never reacts.
        evo_match_id: null,
        create_in_evo: false,
        // `amigos_tipos_abonos_ids` and `amigos_descuentos_abonos_ids`
        // declared as defaults for the same trap: the multiselects in
        // EventoAbonados.vue under `amigo_abonado` (Tipos de abonos no
        // permitidos + Precios no permitidos) assign to these keys via
        // @input handlers. Without the defaults, the keys are created by
        // late assignment on the reactive Proxy, so Vue 2.7's
        // defineProperty-based reactivity doesn't track them and the
        // `descuentos` computed doesn't re-evaluate when the user selects
        // items in the first multiselect.
        amigos_tipos_abonos_ids: [],
        amigos_descuentos_abonos_ids: [],
        // `tipos_abonos_ids` and `tipos_abonos_permitidos_ids` declared for
        // the same trap: these are the two multiselects under the
        // "Activar día del abonado" block (Tipos de abonos que pueden
        // comprar entrada + Tipos de abonos que pueden acceder sin comprar
        // entrada). The @input handlers assign to these keys on every
        // selection change; without the defaults, the keys are created by
        // late assignment on the reactive Proxy and Vue 2.7's reactivity
        // doesn't track them — the user's selections silently disappear
        // on the next re-render because the BaseMultiselect's watcher on
        // `props.value` reads `undefined` and resets `localValue` to `[]`.
        tipos_abonos_ids: [],
        tipos_abonos_permitidos_ids: [],
        // `jornada` declared as default for the same reason: when the user
        // types into the BaseInputNumber in EventoBasicInfo.vue, the
        // `@input` handler assigns `localData.jornada = val`. Without the
        // default, the key is created by late assignment on the reactive
        // Proxy; Vue 2.7's defineProperty-based reactivity doesn't track
        // it, so the `:value="localData.jornada"` binding can desync from
        // the native input value on the next re-render, leaving the user
        // unable to type. EDIT pre-populates via `...props.evento` and
        // overwrites this null; CREAR leaves it null until the user types.
        jornada: null,
        // `name` and `meta_title` declared as defaults for the same
        // Vue 2.7 reactivity trap: the watcher in useEventFormWatchers.js
        // that auto-mirrors `localData.name` into `localData.meta_title`
        // needs both keys to be reactive Proxy keys at setup time,
        // otherwise it never fires after the user types in the titulo
        // input. The :value bindings in EventoBasicInfo / EventoScheduling
        // also benefit from having these declared up-front.
        name: null,
        meta_title: null,
        // `meta_description` declared for the same trap (it gets
        // auto-populated by the watcher that mirrors `localData.synopsis`
        // into it, and the :value binding in EventoScheduling needs to
        // react to that update).
        meta_description: null,
        // `author` (subtitulo) declared for the same trap: the meta_title
        // watcher now reads BOTH `name` and `author`, and the subtitulo
        // BaseInputText in EventoBasicInfo uses the same `@input="val =>
        // localData.author = val"` pattern. Without this default, the
        // key is created by late assignment on the reactive Proxy and the
        // watcher never re-evaluates after the user types.
        author: null,
        // `sga_id` and `sga_product_id` declared for the same Vue 2.7
        // reactivity trap: on CREATE there is no `sga_id` in `props.evento`,
        // so `FormEvento.vue`'s `onLfpSgaSelected` creates the key by late
        // assignment on the reactive Proxy. Vue 2.7's defineProperty-based
        // reactivity doesn't track late-added keys, so the
        // `v-if="localData.sga_id"` in FormEvento.vue never re-evaluates and
        // the SgaProductPicker stays hidden until an unrelated re-render
        // happens to re-read the key. Declaring them up-front makes both the
        // `v-if` and the `:sga-product-id` binding reactive from mount.
        sga_id: null,
        sga_product_id: null,
        // `lfp_id` declared for the same Vue 2.7 reactivity trap: on CREATE
        // an LFP club has no `lfp_id` in props.evento, so onLfpSgaSelected's
        // late assignment went untracked by the defineProperty-based
        // reactivity — the LfpEventPicker's external-sync watcher
        // (watch(() => props.evento.lfp_id, ...)) never fired, so the select
        // kept painting the stale event when Experiencia cleared the state.
        lfp_id: null,
        // `importe_monedero_abonado` and `porcentaje_monedero_abonado`
        // declared for the same trap: these are the two BaseInputNumber
        // inputs that appear inside the conditional block under
        // `permitir_liberacion_asientos` in EventoTicketing.vue. Both use
        // `@input="val => { localData.X = val; ... }"` to mutate localData
        // on every keystroke, and `:value="localData.X"` to render the
        // current value. Without the defaults, the keys are created by
        // late assignment on the reactive Proxy and Vue 2.7's
        // defineProperty-based reactivity doesn't track them, leaving
        // the user unable to type. EDIT pre-populates via `...props.evento`
        // and overwrites these nulls; CREAR leaves them null until the
        // user types.
        importe_monedero_abonado: null,
        porcentaje_monedero_abonado: null,
        // `cerrar_butacas_abonados_no_confirmados` declared for the same
        // trap: this is the conditional toggle that appears under
        // "Cerrar butacas a abonados no confirmados" in EventoAbonados.vue.
        // Without the default, the key is created by late assignment on
        // the reactive Proxy, the ToggleSwitch doesn't re-render to reflect
        // the new value, and the user has to click ANOTHER toggle first
        // to force a re-render before this one updates visually.
        cerrar_butacas_abonados_no_confirmados: false,
        ...props.evento
    });

    // NOTA: el estado de "guardando" NO vive aquí. Lo posee el entrypoint
    // (Crear.vue / Editar.vue) y entra al formulario por `props.loading`, que es
    // la única fuente de verdad del botón CONTINUAR (:disabled + "Guardando...").
    // Antes se declaraba aquí un `loading = ref(false)` que nadie actualizaba y
    // que FormEvento.vue desestructuraba con el mismo nombre, SOMBREANDO
    // `props.loading`: el template leía un ref siempre false, así que el botón
    // podía aceptar un segundo envío y nunca mostraba "Guardando...".
    // Si algún día el composable necesita exponer su propio estado de guardado,
    // debe llamarse distinto (p. ej. `saving`) para no colisionar con la prop.

    // === Custom rules de vee-validate para cross-validation cliente ===
    // vee-validate 3.x NO acepta funciones inline como `:rules` (su
    // normalizeRules exige string u objeto). Las funciones hay que
    // registrarlas globalmente con `extend()` y luego referenciarlas
    // por nombre. Aquí las registramos dentro del setup() para que la
    // closure capture el `localData` reactivo de esta instancia del
    // form. `RuleContainer.extend` sobreescribe si ya existe, así que
    // es idempotente y seguro llamarlo en cada mount del componente.
    // Todas las rules usan `computesRequired: true` para que vee-validate las
    // ejecute INCLUSO cuando el campo está vacío. Sin esto, vee-validate
    // skipea los campos vacíos no requeridos (`shouldSkip = !isRequired
    // && isEmpty` en su `_shouldSkip`), y nuestras cross-validations nunca
    // se dispararían al dejar el otro campo vacío. El objeto retornado
    // lleva `valid` (boolean) y opcionalmente `required` (boolean) —
    // vee-validate usa `required` para decidir si skipear la validation
    // cuando el campo está vacío. Los mensajes custom (en `message`) se
    // usan como default cuando la rule devuelve `{ valid: false }` — al
    // haber un único mensaje por rule, cubre tanto el caso "campo
    // vacío obligatorio" como el caso "orden inválido".
    extend('rangoAbonadoDesde', {
        computesRequired: true,
        message: 'Desde es obligatorio si Hasta tiene valor y no puede ser mayor que Hasta',
        validate(value) {
            const desde = value;
            const hasta = localData.abonado_hasta;
            const hastaHasValue = hasta != null && hasta !== '';
            const desdeEmpty = desde == null || desde === '';
            if (hastaHasValue && desdeEmpty) {
                return { valid: false, required: true };
            }
            const desdeNum = Number(desde);
            const hastaNum = Number(hasta);
            if (desdeNum && hastaNum && desdeNum > hastaNum) {
                return { valid: false, required: true };
            }
            return { valid: true, required: hastaHasValue };
        }
    });
    extend('rangoAbonadoHasta', {
        computesRequired: true,
        message: 'Hasta es obligatorio si Desde tiene valor y no puede ser menor que Desde',
        validate(value) {
            const desde = localData.abonado_desde;
            const hasta = value;
            const desdeHasValue = desde != null && desde !== '';
            const hastaEmpty = hasta == null || hasta === '';
            if (desdeHasValue && hastaEmpty) {
                return { valid: false, required: true };
            }
            const desdeNum = Number(desde);
            const hastaNum = Number(hasta);
            if (desdeNum && hastaNum && desdeNum > hastaNum) {
                return { valid: false, required: true };
            }
            return { valid: true, required: desdeHasValue };
        }
    });
    extend('acompanantesMin', {
        computesRequired: true,
        message: 'El mínimo es obligatorio si el máximo tiene valor y no puede ser mayor que el máximo',
        validate(value) {
            const min = value;
            const max = localData.n_acompanantes;
            const maxHasValue = max != null && max !== '';
            const minEmpty = min == null || min === '';
            if (maxHasValue && minEmpty) {
                return { valid: false, required: true };
            }
            const minNum = Number(min);
            const maxNum = Number(max);
            if (minNum && maxNum && minNum > maxNum) {
                return { valid: false, required: true };
            }
            return { valid: true, required: maxHasValue };
        }
    });
    extend('acompanantesMax', {
        computesRequired: true,
        message: 'El máximo es obligatorio si el mínimo tiene valor y no puede ser menor que el mínimo',
        validate(value) {
            const min = localData.n_acompanantes_min;
            const max = value;
            const minHasValue = min != null && min !== '';
            const maxEmpty = max == null || max === '';
            if (minHasValue && maxEmpty) {
                return { valid: false, required: true };
            }
            const minNum = Number(min);
            const maxNum = Number(max);
            if (minNum && maxNum && minNum > maxNum) {
                return { valid: false, required: true };
            }
            return { valid: true, required: minHasValue };
        }
    });
    // Cross-validation para "Activar descuentos para amig@s de los abonad@s":
    // Tipos de abonos no permitidos y Precios no permitidos son all-or-nothing.
    // Si uno tiene selección, el otro es obligatorio. Se aplican en pares
    // simétricos sobre los dos BaseMultiselect del bloque `amigo_abonado`.
    extend('amigosTiposRequiereAmigosDescuentos', {
        computesRequired: true,
        message: 'Si seleccionas Precios no permitidos, debes seleccionar también Tipos de abonos no permitidos',
        validate() {
            const tipos = localData.amigos_tipos_abonos_ids || [];
            const descuentos = localData.amigos_descuentos_abonos_ids || [];
            const tiposEmpty = tipos.length === 0;
            const descuentosHasValue = descuentos.length > 0;
            if (descuentosHasValue && tiposEmpty) {
                return { valid: false, required: true };
            }
            return { valid: true, required: descuentosHasValue };
        }
    });
    extend('amigosDescuentosRequiereAmigosTipos', {
        computesRequired: true,
        message: 'Si seleccionas Tipos de abonos no permitidos, debes seleccionar también Precios no permitidos',
        validate() {
            const tipos = localData.amigos_tipos_abonos_ids || [];
            const descuentos = localData.amigos_descuentos_abonos_ids || [];
            const descuentosEmpty = descuentos.length === 0;
            const tiposHasValue = tipos.length > 0;
            if (tiposHasValue && descuentosEmpty) {
                return { valid: false, required: true };
            }
            return { valid: true, required: tiposHasValue };
        }
    });
    // Cross-validation para la "Fecha de apertura de venta" (`cuenta_atras`)
    // del bloque "Cerrar venta online". La apertura de la venta online no
    // puede ser posterior a la fecha de fin del evento (`end_event_date`).
    // El atributo `max` nativo del `datetime-local` NO impide teclear una
    // fecha mayor a mano, así que esta rule aporta el error visible que
    // bloquea el submit. Si aún no hay fecha de fin definida, se exige
    // rellenarla antes de poder fijar una apertura (el input ya está
    // deshabilitado en ese caso, pero la rule cubre el caso residual).
    extend('fechaAperturaVenta', {
        computesRequired: true,
        message: (field, params) => {
            const fin = localData.end_event_date;
            return fin == null || fin === ''
                ? 'Para fijar la fecha de apertura de venta es necesario indicar la fecha de fin del evento.'
                : 'La fecha de apertura de venta no puede ser posterior a la fecha de fin del evento.';
        },
        validate(value) {
            const sinFin = localData.end_event_date == null || localData.end_event_date === '';
            const aperturaEmpty = value == null || value === '';
            // Sin fecha de apertura no hay nada que validar (no es obligatoria en sí).
            if (aperturaEmpty) {
                return { valid: true, required: false };
            }
            // Apertura fijada pero el evento aún no tiene fecha de fin → exigirla.
            if (sinFin) {
                return { valid: false, required: false };
            }
            const aperturaDate = new Date(value);
            const finDate = new Date(localData.end_event_date);
            if (Number.isNaN(aperturaDate.getTime()) || Number.isNaN(finDate.getTime())) {
                return { valid: true, required: false };
            }
            return {
                valid: aperturaDate <= finDate,
                required: false
            };
        }
    });

    // Cross-validation para el SgaProductPicker en teatros SGA: los productos
    // asociados al evento SGA son obligatorios. La rule vive aquí (no en el
    // componente) porque vee-validate rules are registered globally; it reads
    // `localData.sga_product_id`, which `onSgaProductSelected` writes.
    // Empty/null product id → invalid (required semantics).
    extend('sgaProductoRequerido', {
        computesRequired: true,
        message: 'Selecciona un producto asociado al evento SGA.',
        validate(value) {
            const empty = value == null || value === '';
            return { valid: !empty, required: true };
        }
    });

    // Cross-validation "Entradas nominativas": son obligatorias cuando el
    // evento tiene un Evento SGA/LFP seleccionado (la selección las activa
    // automáticamente). Si se activa "Experiencia", el evento SGA/LFP se
    // limpia (watcher en useEventFormWatchers.js) y las nominativas dejan de
    // ser obligatorias — por eso la rule valida contra el estado ACTUAL de
    // localData: hay evento seleccionado && nominativas apagadas → inválido.
    extend('solicitarDatosRequerido', {
        computesRequired: true,
        message: 'Las entradas nominativas son obligatorias cuando hay un Evento SGA/LFP seleccionado.',
        validate(value) {
            const tieneEvento = !!(localData.sga_id || localData.lfp_id);
            const nominativas = value === true || value === 1;
            return { valid: !tieneEvento || nominativas, required: tieneEvento };
        }
    });

    // === User-edited flags for auto-generated fields ===
    // Tracks whether the user has manually typed into `meta_title` (or it
    // came pre-populated from the API in EDIT flow). When false, the
    // watcher in useEventFormWatchers.js auto-mirrors `name + author`
    // into `localData.meta_title`. Once the user touches the field (or
    // the EDIT flow shipped a value), we stop overwriting their input.
    // Initial state: true ONLY if meta_title was actually customised in a
    // previous session — i.e. it differs from what auto-fill would produce
    // given current name + author (truncated to 80 chars). If the previous
    // auto-fill value is intact, the flag stays false so future changes to
    // name/author continue to drive meta_title. CREATE starts at false
    // (meta_title is null at initial state).
    const autoFillInitial = () => {
        const parts = [localData.name, localData.author].filter(Boolean);
        const combined = parts.join(' - ');
        return combined
            ? (combined.length > 80 ? combined.substring(0, 80) : combined)
            : null;
    };
    const metaTitleUserModified = ref(
        localData.meta_title != null
        && localData.meta_title !== ''
        && localData.meta_title !== autoFillInitial()
    );

    function markMetaTitleAsUserModified() {
        metaTitleUserModified.value = true;
    }

    // meta_description is intentionally NOT auto-filled — the event
    // creator writes it manually. The reactive default stays null so the
    // :value binding in EventoScheduling.vue reacts when the user types.
    // EDIT flow's API value lands via `...props.evento` and stays put.

    // === Computed ===
    const configuracion = computed(() => store.getters.getConfig);
    const manager = computed(() => store.getters.getFooter);
    const teatro = computed(() => store.getters.getTeatro);
    const recinto = computed(() => store.getters.getRecinto);
    const lista = computed(() => store.getters.getListaActiva);

    const maxPreventaDate = computed(() => {
        if (!localData.start_event_date) return null;
        const eventDate = new Date(localData.start_event_date);
        eventDate.setHours(eventDate.getHours() - 1);
        return eventDate;
    });

    const listaErrores = computed(() => Object.values(props.errorsPeticion || {}).flat());

    const shouldHideSgaPickers = computed(() => !!(teatro.value?.sga && !!localData.experiencia));

    // Cross-field flag for the sports-only trio (equipo local, equipo visitante,
    // competición). If ANY of the three carries a value, the event is a match
    // and the image becomes optional: the venue/club supplies the artwork in
    // that case, so forcing a manual upload blocks the flow for no reason.
    // When none is filled (and for non-sports tenants, where EventoBasicInfo
    // hides the trio entirely), the image stays required.
    //
    // Same predicate EventoBasicInfo uses to make the trio mutually required,
    // kept here as the single source of truth so both cards agree.
    const sportsFieldsFilled = computed(() =>
        Boolean(localData.equipo_local_id || localData.equipo_visitante_id || localData.division_id)
    );

    // Image requirement, single source of truth for the submit gate
    // (FileDropzone's `rules` in EventoScheduling). The image is mandatory
    // only for events that reach the public catalog — published (1),
    // cancelled (2) or postponed (3). A draft (status 0) may be saved with
    // no image so the manager can create it first and complete it later.
    // The sports trio keeps its exemption: when equipo/competición is set,
    // the artwork comes from the venue, so no manual upload is demanded.
    //
    // `status` can be an id, an `{ id, nombre }` object (EDIT hydration before
    // the first save) or missing (CREAR until onMounted seeds the draft), so
    // normalize to an id first; anything that is not a published/cancelled/
    // postponed state counts as a draft and does not require an image.
    const imageRequired = computed(() => {
        const status = localData.status;
        const statusId = status && typeof status === 'object' ? status.id : status;
        const isNonDraft = statusId === 1 || statusId === 2 || statusId === 3;
        return isNonDraft && !sportsFieldsFilled.value;
    });

    // AvanzaEsp: la validez del vínculo con el partido se deriva de la
    // selección ACTUAL, así que el mensaje desaparece solo al corregir sin
    // esperar a un nuevo submit. Misma regla pura que usaba el formulario
    // legacy, expuesta aquí para que FormEvento (gate del submit) y
    // EventoBasicInfo (mensaje) lean la misma fuente.
    const errorEvo = computed(() => {
        const evo = validateEvoRequirement({
            evoEnabled: !!teatro.value?.evo_enabled,
            experiencia: !!localData.experiencia,
            eventoId: localData.id,
            evoMatchId: localData.evo_match_id,
            createInEvo: localData.create_in_evo
        });
        return evo.required && !evo.valid;
    });

    // Regla de vee-validate para el SELECT de partidos: le da su propio estado
    // de error (borde rojo + mensaje + atributo ancla del scroll) igual que el
    // resto de campos del formulario, en lugar de un mensaje suelto aparte.
    // Vive aquí y no en el componente porque las rules se registran globalmente,
    // y lee el mismo contrato puro que `errorEvo` para que el select y el gate
    // del submit nunca discrepen.
    //
    // `message` es una función: el texto depende de si se está creando el evento
    // (donde vale marcar "crear partido en AvanzaEsp") o editándolo (donde hay
    // que vincular uno existente).
    extend('evoPartidoRequerido', {
        computesRequired: true,
        message() {
            return localData.id
                ? 'Debes vincular un partido de AvanzaEsp para este evento'
                : 'Debes seleccionar un partido de AvanzaEsp o marcar "Crear partido en AvanzaEsp"';
        },
        validate() {
            const evo = validateEvoRequirement({
                evoEnabled: !!teatro.value?.evo_enabled,
                experiencia: !!localData.experiencia,
                eventoId: localData.id,
                evoMatchId: localData.evo_match_id,
                createInEvo: localData.create_in_evo
            });

            if (!evo.required) {
                return { valid: true, required: false };
            }

            return { valid: evo.valid, required: true };
        }
    });

    const cesionTemporalBloqueadaPor = computed(() => {
        if (!localData.recinto || localData.recinto === 'false') return 'requiere recinto';
        if (!localData.cerrar_butacas_abonados) return 'requiere cerrar butacas de abonados';
        if (localData.descuento_abonado) return 'no puede coexistir con el día abonado';
        return null;
    });

    // Mismo motivo pero con texto más explicativo, pensado para tooltips
    // (los toasts siguen usando `cesionTemporalBloqueadaPor` para no perder brevedad).
    const cesionTemporalMensajeTooltip = computed(() => {
        if (!localData.recinto) {
            return 'Para habilitar la cesión temporal del asiento, primero debes asignar un recinto al evento.';
        }
        if (!localData.cerrar_butacas_abonados) {
            return 'Para habilitar la cesión temporal del asiento, primero debes activar la opción "Cerrar las butacas de los abonados" en la sección de abonados.';
        }
        return null;
    });

    const puedeConfigurarCesionTemporal = computed(() => !cesionTemporalBloqueadaPor.value);

    const descuentos = computed(() => {
        const result = [];
        (localData.amigos_tipos_abonos_ids || []).forEach(item => {
            (item.descuentos || []).forEach(descuento => result.push(descuento));
        });
        return result;
    });

    // === Compose sub-composables ===
    const lookups = useEventFormLookups();
    const {
        clubes, divisiones, tipos, recintos, tags, eventos, eventosLoading,
        getClubes, getDivisiones, getRecintos, getTipos, getTags, getEventos,
        loadAll, lookupsLoading, lookupsErrors, lookupsHasErrors
    } = lookups;

    // Submit needs the cesion sync helper, so we set up the watchers AFTER
    // we have a placeholder sync. To avoid a chicken-and-egg, build a
    // temporary sync that we'll rebind once the submit composable returns.
    let syncCesionTemporalAsiento = () => {};

    const watchers = useEventFormWatchers(localData, {
        getEventos,
        syncCesionTemporalAsiento: () => syncCesionTemporalAsiento(),
        textoCondicionesLegalesPreventaDefault,
        metaTitleUserModified,
        cesionTemporalBloqueadaPor
    });

    const submitHelpers = useEventFormSubmit(localData, {
        emit,
        cesionTemporalBloqueadaPor,
        originalEvento: props.evento
    });

    // Now bind the real sync to the watcher closure.
    syncCesionTemporalAsiento = submitHelpers.syncCesionTemporalAsiento;

    const {
        normalizeCesionTemporalAsiento,
        syncCesionTemporalAsiento: boundSync,
        procesarEditoresQuill,
        crearEvento,
        submit
    } = submitHelpers;

    // === Local helpers (not submit / lookup concerns) ===
    // Keeps `localData.place_obj` in sync with `localData.place_id` so the
    // submit-time transformation in Crear.vue / Editar.vue (which overwrites
    // `place_id` from `place_obj.id` to derive an id from the hydrated object)
    // doesn't accidentally clobber a value the user just typed.
    function changeAddress() {
        if (localData.place_obj?.address) {
            localData.address = localData.place_obj.address;
        }
        // Re-sync place_obj to whichever recinto has the current place_id.
        // Cheap safety net; if some watcher elsewhere changed place_id without
        // updating place_obj, this brings them back in sync.
        if (localData.place_id != null && recintos.value?.length) {
            const found = recintos.value.find(r => r.id === localData.place_id);
            if (found && localData.place_obj?.id !== found.id) {
                localData.place_obj = found;
            }
        }
    }

    function setRecinto() {
        if (localData.recinto) {
            // Use the default recinto from the global vuex getter only when
            // there's no place_id yet (initial toggle ON in the create flow).
            // Otherwise keep whatever the user already picked.
            if (!localData.place_id && recinto.value) {
                localData.place_id = recinto.value.id;
                localData.place_obj = recinto.value;
            }
        }
    }

    function clearFecha() {
        if (new Date(localData.start_event_date) > new Date(localData.end_event_date)) {
            localData.end_event_date = '';
        }
    }

    function setSgaData(data) {
        localData.name = data.name;
        localData.author = data.season?.name;
    }

    function imagenSubida(file, response) {
        localData.image = response;
    }

    function imagenBorrada() {
        // Clear the bound image so the `rules="required"` ValidationProvider
        // sees a null value (its watcher in EventoScheduling will re-validate
        // and the submit gate reopens). Without this, a user who uploads and
        // then deletes the image would still submit with a stale image value.
        localData.image = null;
    }

    // Backend error scroll is handled by FormEvento.vue via watch(listaErrores)
    // — the entrypoint doesn't have access to the erroresBackend template ref.

    // === Initialization ===
    onMounted(() => {
        // `loadAll()` owns the aggregated lookup lifecycle (loading + errors).
        // It never rejects: a failing lookup degrades to an empty select, and the
        // failure is surfaced through `lookupsHasErrors`/`lookupsErrors` instead of
        // being discarded (the previous `Promise.allSettled` dropped its results).
        loadAll({ listaAbonadosId: props.evento.lista_abonados_id });


        if (process.env.VUE_APP_TEST_APP == 1) {
            localData.test = 1;
        }

        if (!localData.place_id && recinto.value && !localData.entradas_compartidas) {
            localData.place_id = recinto.value.id;
        }

        // Pre-select the teatro's default province when the form has no
        // province yet (CREATE flow). EDIT already brings it from the API.
        if (!localData.provincia && teatro.value?.provincia) {
            localData.provincia = teatro.value.provincia;
        }

        if (localData.provincia && typeof localData.provincia === 'object') {
            localData.provincia_id = localData.provincia.id;
        } else if (localData.provincia) {
            localData.provincia_id = localData.provincia;
        }

        // Pre-select the teatro's default location when it ships one.
        // Mirrors how EditDetalleResource:163 populates location/location_id
        // from the saved event. Accepts either an object (teatro.location /
        // teatro.localidad) or a bare id (teatro.location_id); if none is
        // provided today, this block is a no-op and the user picks it
        // manually from the multiselect.
        if (!localData.location_id && teatro.value) {
            const teatroLocation = teatro.value.location || teatro.value.localidad;
            const teatroLocationId = teatro.value.location_id || teatroLocation?.id;
            if (teatroLocationId) {
                localData.location_id = teatroLocationId;
                if (teatroLocation && typeof teatroLocation === 'object') {
                    localData.location = teatroLocation;
                }
            }
        }

        // Pre-populate synopsis with default copy for sports / LFP tenants.
        // CREATE only — EDIT already brings synopsis from the API. This
        // used to live in Crear.vue#onMounted but landed on `evento` AFTER
        // FormEvento had already spread it into localData, so the
        // QuillEditor never saw it. Mutating here reaches localData
        // before the first paint of EventoTextEditors.
        //
        // Strings copied verbatim from master (src/views/backend/events/Crear.vue)
        // so tenants with sports/LFP managers see the same default copy
        // they used to get before the refactor.
        // Sports/LFP defaults only apply when the manager is the sports
        // tenant (tipo == 1). For any other tenant (tipo == 0, e.g. teatros)
        // synopsis stays empty regardless of `lfp`, which is reserved
        // for the sports-only copy. Two independent `if`s mirror master.
        if (!localData.synopsis && manager.value?.tipo == 1) {
            localData.synopsis = `
                <h3>Venta Oficial de entradas</h3>
                <p class="mb-3">Disfruta el mejor partido de tu equipo comprando tus entradas en tan sólo dos minutos, una compra fácil, segura y rápida para vivir una experiencia única. No olvides en cumplir la normativa vigente en la ejecución del encuentro.</p>

                <h3>Normativa</h3>
                <p>RD 203/2010 REGLAMENTO DE PREVENCIÓN DE LA VIOLENCIA, EL RACISMO, LA XENOFOBIA Y LA INTOLERANCIA EN EL DEPORTE. Recinto deportivo video vigilado para la seguridad de los asistentes y participantes en el encuentro. Causas que impiden el acceso o la permanencia en el recinto deportivo. a) Participar en altercados, riñas, peleas o desórdenes públicos. b) Introducir, portar o utilizar cualquier clase de armas o de objetos que pudieran producir los mismos efectos. c) Objetos de peso superior a 500g susceptibles de utilizar como proyectiles. d) Introducir o estar en posesión de bengalas, petardos, explosivos o, en general productos inflamables. e) Introducir o vender cualquier clase de bebidas alcohólicas, estupefacientes, psicotrópicos, estimulantes. f) Introducir, exhibir o elaborar pancartas, banderas, símbolos, u otras señas que inciten a la violencia o terrorismo. g) Realizar cánticos, expresiones, sonidos o aptitudes que inciten a la violencia o al terrorismo. h) Irrumpir en el terreno de juego. i) Haber sido sancionado con la prohibición de acceso a cualquier recinto deportivo en tanto no se haya extinguido la sanción. LA ADQUISICIÓN DE ESTA ENTRADA REPRESENTA LA ACEPTACIÓN DE LAS SIGUIENTES CONDICIONES: -La Organización no garantiza la autenticidad de esta entrada si no ha sido adquirida en los puntos de venta oficiales. No está permitida la reventa de esta entrada. Toda entrada enmendada, rota, sospechosa de falsificación o adquirida ilícitamente autorizará al organizador a privar del acceso al portador de la misma. -El personal de seguridad y control de accesos, se reserva el derecho a retirar los tapones o cubierta de todos los envases, así como todos aquellos recipientes que por su capacidad y rigidez puedan resultar peligrosos. - Conservar la entrada hasta la salida del recinto. La salida del recinto implica pérdida del derecho a entrar nuevamente, salvo en aquellos eventos que el organizador disponga lo contrario</p>
            `;

            // Master uses two independent `if`s so a tenant that is both
            // sports AND LFP ends up with the LFP copy. Match that: this
            // runs even though the guard above already implies tipo == 1.
            if (manager.value?.lfp == 1) {
                localData.synopsis = `
                    <h3>Venta Oficial de entradas</h3>
                    <p class="mb-3">Disfruta el mejor partido de tu equipo comprando tus entradas en tan sólo dos minutos, una compra fácil, segura y rápida para vivir una experiencia única. No olvides en cumplir la normativa vigente en la ejecución del encuentro.</p>

                    <h3>Normativa</h3>
                    <p>Causas que impiden el acceso o la permanencia en el recinto deportivo: a) Participar en altercados, riñas, peleas o desórdenes públicos. b) Introducir, portar o utilizar cualquier clase de armas o de objetos que pudieran producir los mismos efectos, como elementos punzantes, cortantes, o de peso superior a 500 gramos/mililitros susceptibles de utilizarse como proyectiles, tales como alimentos en recipientes rígidos, bebidas embotelladas o sus envases. c) Introducir o estar en posesión de bengalas, petardos, explosivos o, en general, productos inflamables, fumíferos o corrosivos y dispositivos pirotécnicos. d) Encontrarse bajo los efectos de bebidas alcohólicas, estupefacientes, psicotrópicos, estimulantes o sustancias análogas. e) Introducir o vender cualquier clase de bebidas alcohólicas, sustancias estupefacientes, psicotrópicas, estimulantes o análogas. f) Introducir, exhibir o elaborar pancartas, banderas, símbolos u otras señales con mensajes que inciten a la violencia o al terrorismo, o en cuya virtud una persona o grupo de ellas sea amenazada, insultada o vejada por razón de su origen racial o étnico, religión o convicciones, discapacidad, edad, sexo u orientación sexual. g) Realizar cánticos, expresiones, sonidos o actitudes que inciten a la violencia o al terrorismo, o que pretendan vejar a una persona o grupo de ellas por razón de su raza o etnia, discapacidad, religión o convicciones, sexo u orientación sexual. h) Irrumpir en el terreno de juego. i) Haber sido sancionado o suspendido con la prohibición de acceso a cualquier recinto deportivo en tanto no se haya extinguido la sanción. j) No haber abonado el precio y tributos de la entrada, no presentarla o no conservarla durante el evento k)Transmitir, distribuir, ceder, vender (o ayudar a hacerlo) cualquier descripción, anotación, imagen, video, audio, dato, estadística u otra forma de reproducción del evento, excepto para uso personal y privado. l) Aquellas otras previstas en la legislación o normativa aplicable, en particular la Ley 19/2007, de 11 de julio, el Real Decreto 203/2010, de 26 de febrero, el Reglamento para la venta de abonos y entradas de LaLiga y el Reglamento interno del recinto deportivo donde se celebre el evento. Para más información sobre el tratamiento de datos en la identificación y verificación de identidad, por favor, diríjase al Club/SAD emisor de esta entrada.</p>
                `;
            }
        }

        if (!localData.status && localData.status !== 0) {
            localData.status = estados.value[0].id;
        }

        if (!localData.address && !localData.entradas_compartidas) {
            localData.address = recinto.value?.address;
        }

        if (localData.entradas_compartidas) {
            getEventos(localData.start_event_date);
        }

        if (localData.preventa_configurada && !localData.texto_condiciones_legales_preventa) {
            localData.texto_condiciones_legales_preventa = textoCondicionesLegalesPreventaDefault;
        }

        normalizeCesionTemporalAsiento();
        boundSync();
    });

    return {
        // Reactive state
        localData,

        // Lookup data
        clubes,
        divisiones,
        estados,
        tipos,
        recintos,
        tags,
        eventos,
        eventosLoading,
        limitesTiempo,

        // Computed
        configuracion,
        manager,
        teatro,
        recinto,
        lista,
        maxPreventaDate,
        listaErrores,
        shouldHideSgaPickers,
        sportsFieldsFilled,
        imageRequired,
        errorEvo,
        cesionTemporalBloqueadaPor,
        cesionTemporalMensajeTooltip,
        puedeConfigurarCesionTemporal,
        descuentos,

        // Methods
        submit,
        changeAddress,
        setRecinto,
        clearFecha,
        getClubes,
        getDivisiones,
        getRecintos,
        getTipos,
        getTags,
        getEventos,
        loadAll,
        lookupsLoading,
        lookupsErrors,
        lookupsHasErrors,
        imagenSubida,
        imagenBorrada,
        setSgaData,
        normalizeCesionTemporalAsiento,
        syncCesionTemporalAsiento: boundSync,
        procesarEditoresQuill,
        crearEvento,
        markMetaTitleAsUserModified
    };
}